import React, { useLayoutEffect, useState } from "react";
import dayjs from "dayjs";

import variable from "json/variable.json";

import { ReactComponent as Dropdown } from "assets/dropdown_icon_open.svg";

import {
  useRecoilValue,
  useRecoilValueLoadable,
  useSetRecoilState,
} from "recoil";
import {
  asyncGetAPIConnects,
  channelTalkUpdate,
  deleteAppStore,
  deleteChannelTalk,
  deleteGooglePlay,
  issueAppStore,
  issueChannelTalk,
  issueGooglePlay,
  renewAppStore,
  renewGooglePlay,
} from "lib/store/connect";
import { currentProject } from "lib/store/project";
import { customToastState, errorToastState } from "lib/store/common";

import { Button, Radio } from "@atsent/components";

import { ProgressButton } from "components/atom/ProgressButton";

import "./APIConnect.scss";

export type APIConnectProps = {
  cx?: string;
};

const APIItem: React.FC<{ state: string; data: any }> = ({ state, data }) => {
  const [isOpen, setOpen] = useState(false);
  const pid = useRecoilValue(currentProject);
  const refreshAPIs = useSetRecoilState(channelTalkUpdate);

  useLayoutEffect(() => {
    if (!!data?.uuid) {
      setOpen(true);
    }
  }, [data]);

  return (
    <>
      <div className="title top" onClick={() => setOpen((_) => !_)}>
        <span>채널톡</span>
        <Dropdown className={` ${isOpen}`} />
      </div>
      {state === "hasValue" && (
        <div className={`contents ${isOpen}`}>
          <ul>
            <li>
              <span className={"label"}>Hook URL</span>
              <input
                disabled
                defaultValue={
                  data.uuid &&
                  `${
                    variable[process.env.NODE_ENV].backend_url
                  }/sentiment/webhook/channel-talk/?uuid=${data.uuid}`
                }
              />
            </li>
            {data.created_at && (
              <li>
                <span className={"label"}>발급 날짜</span>
                <span className={"small"}>
                  {dayjs(data.created_at).format("YYYY.MM.DD HH:mm:ss")}
                </span>
              </li>
            )}
          </ul>
          <div className="button-wrapper">
            {data.uuid ? (
              <Button
                outline
                cx={"center delete"}
                click={async () => {
                  await deleteChannelTalk(pid);
                  await refreshAPIs(new Date());
                }}
              >
                삭제하기
              </Button>
            ) : (
              <Button
                cx={"center"}
                click={async () => {
                  await issueChannelTalk(pid);
                  await refreshAPIs(new Date());
                }}
              >
                발급하기
              </Button>
            )}
          </div>
        </div>
      )}
    </>
  );
};

const GooglePlayItem: React.FC<{ state: string; data: any }> = ({
  state,
  data,
}) => {
  const [isOpen, setOpen] = useState(false);
  const pid = useRecoilValue(currentProject);
  const refreshAPIs = useSetRecoilState(channelTalkUpdate);
  const [text, setText] = useState("");
  const [hl, setHl] = useState("ko");
  const setToast = useSetRecoilState(customToastState);
  const setErrorToast = useSetRecoilState(errorToastState);

  useLayoutEffect(() => {
    if (!!data?.app_name) {
      setOpen(true);
    }
  }, [data]);

  return (
    <>
      <div className="title" onClick={() => setOpen((_) => !_)}>
        <span>Google Store App Review</span>
        <Dropdown className={` ${isOpen}`} />
      </div>
      {state === "hasValue" && (
        <div className={`contents ${isOpen}`}>
          <ul>
            <li>
              <span className={"label"}>ID</span>
              <input
                onChange={(e) => setText(e.target.value)}
                disabled={!!data.app_name}
                defaultValue={data.app_name}
              />
            </li>
            <li>
              <span className={"label"}>리뷰 언어</span>
              <form
                className="language-wrapper"
                onChange={(e: any) => setHl(e.target.value)}
              >
                <Radio
                  disabled={!!data.app_name}
                  checked={!!data.app_name ? data.lang === "ko" : true}
                  name={"appstore-lang"}
                  label={"한글"}
                  value={"ko"}
                  inline
                />
                <Radio
                  disabled={!!data.app_name}
                  checked={!!data.app_name && data.lang === "en"}
                  name={"appstore-lang"}
                  label={"영어"}
                  value={"en"}
                  inline
                />
              </form>
            </li>
            {!!data.created_at && (
              <li>
                <span className={"label"}>연동 날짜</span>
                <span className={"small"}>
                  {dayjs(data.created_at).format("YYYY.MM.DD HH:mm:ss")}
                </span>
              </li>
            )}
          </ul>
          <div className="button-wrapper">
            {data.app_name && (
              <>
                <Button
                  outline
                  cx={"center delete left"}
                  click={async () => {
                    await deleteGooglePlay(pid);
                    await refreshAPIs(new Date());
                  }}
                >
                  삭제하기
                </Button>
                <p>
                  <span>
                    매일 자정, 자동으로 새로운 앱 리뷰를 가져옵니다. 추가 갱신이
                    필요하신 경우, ‘갱신하기’ 버튼을 눌러주세요.
                  </span>
                  <br />
                  {!!data.scheduler.last_run_at && (
                    <span className={"bold"}>
                      마지막 갱신 날짜:
                      {dayjs(data.scheduler.last_run_at).format(
                        "YYYY년 MM월 DD일 HH시 mm분 ss초"
                      )}
                    </span>
                  )}
                </p>
              </>
            )}
            {data.app_name ? (
              <Button
                cx={"center"}
                click={async () => {
                  await renewGooglePlay(pid);
                  await refreshAPIs(new Date());
                }}
              >
                갱신하기
              </Button>
            ) : (
              <ProgressButton
                cx={"center"}
                pid={pid}
                ls={{
                  key: "apiConnect",
                  type: "googlePlay",
                }}
                curId={async () => {
                  try {
                    return (
                      (await issueGooglePlay(pid, { id: text, hl })).id ?? 0
                    );
                  } catch {
                    return 0;
                  }
                }}
                onProcessEnd={() => refreshAPIs(new Date())}
                setToast={setToast}
                setErrorToast={setErrorToast}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};
const AppStoreItem: React.FC<{ state: string; data: any }> = ({
  state,
  data,
}) => {
  const [isOpen, setOpen] = useState(false);
  const setToast = useSetRecoilState(customToastState);
  const setErrorToast = useSetRecoilState(errorToastState);
  const pid = useRecoilValue(currentProject);
  const refreshAPIs = useSetRecoilState(channelTalkUpdate);

  const [text, setText] = useState("");
  const [country, setCountry] = useState("kr");

  useLayoutEffect(() => {
    if (!!data?.app_name) {
      setOpen(true);
    }
  }, [data]);

  return (
    <>
      <div className="title" onClick={() => setOpen((_) => !_)}>
        <span>iOS App Store</span>
        <Dropdown className={` ${isOpen}`} />
      </div>
      {state === "hasValue" && (
        <div className={`contents ${isOpen}`}>
          <ul>
            <li>
              <span className={"label"}>ID</span>
              <input
                onChange={(e) => setText(e.target.value)}
                disabled={!!data.app_name}
                defaultValue={data.app_name}
              />
            </li>
            <li>
              <span className={"label"}>리뷰 언어</span>
              <form
                className="language-wrapper"
                onChange={(e: any) => setCountry(e.target.value)}
              >
                <Radio
                  disabled={!!data.app_name}
                  checked={!!data.app_name ? data.country === "kr" : true}
                  name={"appstore-lang"}
                  label={"한글"}
                  value={"kr"}
                  inline
                />
                <Radio
                  disabled={!!data.app_name}
                  checked={!!data.app_name && data.country === "us"}
                  name={"appstore-lang"}
                  label={"영어"}
                  value={"us"}
                  inline
                />
              </form>
            </li>
            {!!data.created_at && (
              <li>
                <span className={"label"}>연동 날짜</span>
                <span className={"small"}>
                  {dayjs(data.created_at).format("YYYY.MM.DD HH:mm:ss")}
                </span>
              </li>
            )}
          </ul>
          <div className="button-wrapper">
            {data.app_name && (
              <>
                <Button
                  outline
                  cx={"center delete left"}
                  click={async () => {
                    try {
                      await deleteAppStore(pid);
                      await refreshAPIs(new Date());
                    } catch (e) {
                      setErrorToast(e);
                    }
                  }}
                >
                  삭제하기
                </Button>
                <p>
                  <span>
                    매일 자정, 자동으로 새로운 앱 리뷰를 가져옵니다. 추가 갱신이
                    필요하신 경우, ‘갱신하기’ 버튼을 눌러주세요.
                  </span>
                  <br />
                  {!!data.scheduler.last_run_at && (
                    <span className={"bold"}>
                      마지막 갱신 날짜:
                      {dayjs(data.scheduler.last_run_at).format(
                        "YYYY년 MM월 DD일 HH시 mm분 ss초"
                      )}
                    </span>
                  )}
                </p>
              </>
            )}
            {data.app_name ? (
              <Button
                cx={"center"}
                click={async () => {
                  await renewAppStore(pid);
                  await refreshAPIs(new Date());
                }}
              >
                갱신하기
              </Button>
            ) : (
              <ProgressButton
                cx={"center"}
                pid={pid}
                ls={{
                  key: "apiConnect",
                  type: "appStore",
                }}
                curId={async () => {
                  try {
                    return (
                      (await issueAppStore(pid, { name: text, country })).id ??
                      0
                    );
                  } catch {
                    return 0;
                  }
                }}
                onProcessEnd={() => refreshAPIs(new Date())}
                setToast={setToast}
                setErrorToast={setErrorToast}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};

export const APIConnect: React.FC<APIConnectProps> = ({ cx = "" }) => {
  const { state, contents } = useRecoilValueLoadable(asyncGetAPIConnects);

  return (
    <section className={`APIConnect ${cx}`}>
      <p>
        여러 채널로부터 데이터를 취합하여 분석합니다. CS 창구를 쉽게
        연동해보세요.
      </p>
      <p>채널 연동</p>
      <div className="Accordion">
        <APIItem state={state} data={contents?.channelTalk} />
        <GooglePlayItem state={state} data={contents?.google_play} />
        <AppStoreItem state={state} data={contents?.app_store} />
      </div>
    </section>
  );
};
